import type { ReactNode } from 'react';
import { dateParts, periodLabel, periodParts } from '@gdec/shared';
import { Modal } from '@/components/Modal';
import type { DateOptions } from './receiptUpload';

export interface PendingDateConfirm {
  /** Stable key so React does not reuse one confirmation's state for the next. */
  key: string;
  dateOptions: DateOptions;
  company: string;
  receipt: string;
  previewUrl: string | null;
}

export type DateChoice = 'original' | 'swapped';

function longDate(iso: string): string {
  const parts = dateParts(iso);
  if (!parts) return iso;
  return new Date(parts.y, parts.m - 1, parts.d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function matchesPeriod(iso: string, period: string): boolean {
  const parts = dateParts(iso);
  if (!parts) return false;
  const { y, m } = periodParts(period);
  return parts.y === y && parts.m === m;
}

export interface DateConfirmModalProps {
  pending: PendingDateConfirm;
  period: string;
  onChoose: (choice: DateChoice) => void;
  onSkip: () => void;
}

/**
 * Philippine receipts mix DD/MM and MM/DD, so "03/08" is genuinely two dates.
 * The requester is the only one who can say which reading is right, and they
 * are told plainly that the reading outside the claim period drops the receipt.
 */
export function DateConfirmModal({
  pending,
  period,
  onChoose,
  onSkip,
}: DateConfirmModalProps): ReactNode {
  const { original, swapped, raw } = pending.dateOptions;
  const originalMatches = matchesPeriod(original, period);
  const swappedMatches = matchesPeriod(swapped, period);
  const label = periodLabel(period);
  const what = pending.receipt || pending.company || 'this receipt';

  return (
    <Modal title="Confirm Receipt Date" onClose={onSkip}>
      <p style={{ fontSize: 12.5, marginTop: 0 }}>
        The date printed on <b>{what}</b> is <b>{raw}</b>, which could be read two ways. Which one
        is correct?
      </p>

      {pending.previewUrl ? (
        <img className="cf-date-preview" src={pending.previewUrl} alt="The uploaded receipt" />
      ) : null}

      <div className="cf-date-options">
        <button
          type="button"
          className={originalMatches ? 'btn btn-good' : 'btn btn-ghost'}
          onClick={() => onChoose('original')}
        >
          {longDate(original)}
          {originalMatches ? " — matches this claim's period" : " — does NOT match this claim's period"}
        </button>
        <button
          type="button"
          className={swappedMatches ? 'btn btn-good' : 'btn btn-ghost'}
          onClick={() => onChoose('swapped')}
        >
          {longDate(swapped)}
          {swappedMatches ? " — matches this claim's period" : " — does NOT match this claim's period"}
        </button>
      </div>

      <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>
        Picking the reading that doesn&apos;t match {label} will reject this receipt from the claim.
      </p>
    </Modal>
  );
}
